import React from 'react';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import LinearProgress from '@mui/material/LinearProgress';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CloseIcon from '@mui/icons-material/Close';
import MergeIcon from '@mui/icons-material/Merge';
import PrintKOTPrint from '@components/Print/PrintKOTPrint';
import OrderServiceTypeToggle from '@components/OrderServiceTypeToggle';

import CartItemRow from './CartItemRow';
import BillingSummaryView from './BillingSummaryView';
import CustomerForm from './CustomerForm';
import type { CartPanelProps } from '../types';
import { getRoomLabelText } from '@src/utilities/common';

const CartPanel: React.FC<CartPanelProps> = ({
    activeTable,
    tableCart,
    activeCartItems,
    billing,
    mobileCartOpen,
    isLoading,
    isOnline,
    canReadTestBilling,
    canPrintKOT,
    checkedGST,
    showGstField,
    customerDetails,
    serviceType,
    selectedRestId,
    roles,
    onServiceTypeChange,
    onToggleGST,
    onCloseMobile,
    onMergeTable,
    onUpdateQty,
    onRemoveItem,
    onDescriptionChange,
    onCheckout,
    onCustomerChange,
    onDiscountChange,
}) => {
    const itemCount = activeCartItems.length;
    const hasRole = roles && selectedRestId && ['admin', 'manager', 'staff'].includes(roles[selectedRestId]?.role);

    return (
        <div className={`posv2-cart ${mobileCartOpen ? 'posv2-cart--open' : ''}`}>
            {/* Header */}
            <div className="posv2-cart__header">
                <div className="posv2-cart__header-left">
                    <h3 className="posv2-cart__title">{getRoomLabelText(activeTable?.split("-")[0], parseInt(activeTable?.split("-")[1]))}</h3>
                    <span className="posv2-cart__item-count">
                        {itemCount} item{itemCount !== 1 ? 's' : ''}
                    </span>
                </div>
                <div className="posv2-cart__header-actions">
                    <Tooltip title="Merge/Move table">
                        <IconButton size="small" onClick={onMergeTable} color="secondary">
                            <MergeIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <div className="posv2-cart__service-type-toggle">
                        <OrderServiceTypeToggle
                            value={serviceType}
                            onChange={onServiceTypeChange}
                        />
                    </div>
                    {canReadTestBilling && (
                        <div className="posv2-cart__gst-toggle">
                            <span className="posv2-cart__gst-label">GST</span>
                            <Switch
                                size="small"
                                color="primary"
                                checked={checkedGST}
                                onChange={(e) => onToggleGST(e.target.checked)}
                            />
                        </div>
                    )}
                    <IconButton
                        size="small"
                        className="posv2-cart__close-mobile"
                        onClick={onCloseMobile}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </div>
            </div>

            {/* Cart Items */}
            <div className="posv2-cart__items">
                {itemCount === 0 && (
                    <div className="posv2-cart__empty">
                        <ShoppingCartIcon className="posv2-cart__empty-icon" />
                        <p>Cart is empty</p>
                        <span>Tap items to add</span>
                    </div>
                )}
                {activeCartItems.map(([key, val]) => (
                    <CartItemRow
                        key={key}
                        itemKey={key}
                        item={val}
                        onIncrement={() => onUpdateQty(key, val, val.selected && val.selected > 5 ? 2 : 1)}
                        onDecrement={() => onUpdateQty(key, val, -1)}
                        onRemove={() => onRemoveItem(key)}
                        canShowKOTDescription={canPrintKOT}
                        onDescriptionChange={onDescriptionChange ? (desc) => onDescriptionChange(key, desc) : undefined}
                    />
                ))}
            </div>

            {/* Footer */}
            {itemCount > 0 && (
                <div className="posv2-cart__footer">
                    <BillingSummaryView summary={billing} />

                    {hasRole && (
                        <CustomerForm
                            details={customerDetails}
                            showGstField={showGstField}
                            discountValue={billing.discount}
                            onChange={onCustomerChange}
                            onDiscountChange={onDiscountChange}
                        />
                    )}

                    {!isOnline && <div className="posv2-cart__offline">No Internet</div>}
                    {isLoading && <LinearProgress color="primary" className="posv2-cart__progress" />}

                    <div className="posv2-cart__actions">
                        {canPrintKOT && (
                            <PrintKOTPrint
                                item={tableCart}
                                tableNumber={activeTable}
                                resId={selectedRestId}
                                serviceType={serviceType}
                                source="pos-v2"
                            />
                        )}
                        <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            size="large"
                            disabled={!isOnline || !billing.totalCost || isLoading}
                            onClick={onCheckout}
                            className="posv2-cart__checkout-btn"
                        >
                            Pay ₹{billing.grandTotal}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default React.memo(CartPanel);
